import React, { useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
// html2canvas loaded dynamically on capture to reduce bundle size
import { Button } from './ui/button';
import { Download, ChevronLeft, ChevronRight, Loader2, Share2, Copy, Palette, RectangleVertical, Square, ArrowLeft } from 'lucide-react';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { useSwipe } from '../hooks/useSwipe';
import { useNativeShare } from '../hooks/useNativeShare';
import { BodyView } from './MuscleBodyMap';
import { FRONT_MUSCLES, BACK_MUSCLES } from '../lib/musclePathData';
import { getDisplayName } from '../lib/displayName';

const FRONT_VB = '0 95 727 1280';
const BACK_VB = '775 85 700 1320';

const FONT = "'Barlow Condensed', sans-serif";
const RATIOS = {
  square: { w: 1080, h: 1080, label: '1:1', previewMax: 340, previewScale: 0.3148, cssAspect: '1/1' },
  story:  { w: 1080, h: 1920, label: '9:16', previewMax: 220, previewScale: 0.2037, cssAspect: '9/16' },
};
const POSITIONS = [
  'top-left', 'top-center', 'top-right',
  'center-left', 'center-center', 'center-right',
  'bottom-left', 'bottom-center', 'bottom-right',
];
const POS_LABEL_KEYS = {
  'top-left': 'storyGenerator.posTopL', 'top-center': 'storyGenerator.posTopC', 'top-right': 'storyGenerator.posTopR',
  'center-left': 'storyGenerator.posMidL', 'center-center': 'storyGenerator.posMidC', 'center-right': 'storyGenerator.posMidR',
  'bottom-left': 'storyGenerator.posBotL', 'bottom-center': 'storyGenerator.posBotC', 'bottom-right': 'storyGenerator.posBotR',
};

function PositionGridIcon({ position }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2.5, width: 20, height: 20 }}>
      {POSITIONS.map(c => (
        <div key={c} style={{ borderRadius: 1, background: c === position ? '#FF5C00' : 'rgba(255,255,255,0.35)' }} />
      ))}
    </div>
  );
}

// Display name mapper — keeps internal keys intact, only changes user-visible text
const dmn = (m) => ({ pectorals: 'Chest', chest: 'Chest', 'upper back': 'Back', back: 'Back' })[String(m).toLowerCase()] || m;

const MC = {
  pectorals: '#ef4444', 'upper back': '#3b82f6', lats: '#3b82f6', delts: '#8b5cf6',
  biceps: '#f59e0b', triceps: '#ec4899', quads: '#22c55e', hamstrings: '#16a34a',
  glutes: '#14b8a6', calves: '#06b6d4', abs: '#f97316', forearms: '#6366f1',
  traps: '#6366f1', adductors: '#ec4899', abductors: '#84cc16',
};

const BG_OPTIONS = [
  { id: 'photo', labelKey: 'storyGenerator.bgPhoto', bg: 'photo', text: '#ffffff' },
  { id: 'transparent', labelKey: 'storyGenerator.bgNone', bg: 'transparent', text: '#ffffff' },
  { id: 'dark', labelKey: 'storyGenerator.bgDark', bg: '#1a1a1a', text: '#ffffff' },
  { id: 'black', labelKey: 'storyGenerator.bgBlack', bg: '#000000', text: '#ffffff' },
  { id: 'orange', labelKey: 'storyGenerator.bgOrange', bg: '#FF5C00', text: '#ffffff' },
  { id: 'navy', labelKey: 'storyGenerator.bgNavy', bg: '#0f172a', text: '#ffffff' },
  { id: 'white', labelKey: 'storyGenerator.bgWhite', bg: '#ffffff', text: '#1a1a1a' },
];

function getTargetMuscles(exercises) {
  const counts = {};
  (exercises || []).forEach(ex => {
    const targets = ex.targetMuscles?.length > 0 ? ex.targetMuscles : [];
    if (targets.length > 0) {
      targets.forEach(tm => { const k = tm.toLowerCase(); counts[k] = (counts[k] || 0) + (ex.sets?.length || 1); });
    } else if (ex.muscle_group) {
      const MAP = { 'chest': 'pectorals', 'back': 'upper back', 'shoulders': 'delts', 'upper arms': 'triceps', 'lower arms': 'forearms', 'legs': 'quads', 'waist': 'abs' };
      const k = MAP[ex.muscle_group.toLowerCase()] || ex.muscle_group.toLowerCase();
      counts[k] = (counts[k] || 0) + (ex.sets?.length || 1);
    }
  });
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return Object.entries(counts).map(([name, sets]) => ({ name, sets, pct: total > 0 ? Math.round((sets / total) * 100) : 0 })).sort((a, b) => b.sets - a.sets);
}

function getPRs(exercises) {
  const prs = [];
  (exercises || []).forEach(ex => {
    let bestVol = 0, bestSet = null;
    ex.sets?.forEach(s => {
      if (s.is_pr || s.pr_types?.length > 0) {
        const vol = (s.weight || 0) * (s.reps || s.completed_reps || 0);
        if (vol > bestVol) {
          bestVol = vol;
          bestSet = { name: ex.name, weight: s.weight, reps: s.reps || s.completed_reps, volume: vol, pr_types: s.pr_types || ['weight'] };
        }
      }
    });
    if (bestSet) prs.push(bestSet);
  });
  return prs;
}

function getVolumeFunFact(vol) {
  if (!vol || vol <= 0) return null;
  if (vol < 500) return { text: "That's like lifting a motorcycle!", emoji: '🏍️' };
  if (vol < 2000) return { text: "That's like lifting a car!", emoji: '🚗' };
  if (vol < 5000) return { text: "That's like lifting an elephant!", emoji: '🐘' };
  if (vol < 10000) return { text: "That's like lifting a truck!", emoji: '🚛' };
  if (vol < 20000) return { text: "That's like lifting a bus!", emoji: '🚌' };
  if (vol < 50000) return { text: "That's like lifting a whale!", emoji: '🐋' };
  return { text: "That's like lifting a plane!", emoji: '✈️' };
}

function formatDuration(min) {
  if (!min) return '0min';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function formatVolume(vol) {
  if (!vol) return '0 kg';
  return `${vol.toLocaleString()} kg`;
}

/* Card wrapper with branding footer */
function StoryCard({ bg, photo, textColor, userName, children, W, H, position = 'bottom-left' }) {
  const isTransparent = bg === 'transparent';
  const isPhoto = bg === 'photo' && !!photo;
  const effectiveTextColor = isPhoto ? '#ffffff' : textColor;
  const isStory = H > W;
  const [vert, horiz] = position.split('-');
  const justifyContent = vert === 'top' ? 'flex-start' : vert === 'bottom' ? 'flex-end' : 'center';
  const alignItems = horiz === 'left' ? 'flex-start' : horiz === 'right' ? 'flex-end' : 'center';
  const needsGradient = isPhoto || isTransparent;
  const gradient = needsGradient
    ? vert === 'bottom'
      ? 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 35%, rgba(0,0,0,0.72) 62%, rgba(0,0,0,0.93) 100%)'
      : vert === 'top'
      ? null
      : 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.75) 100%)'
    : null;
  return (
    <div style={{
      width: W, height: H, fontFamily: FONT, color: effectiveTextColor,
      background: isTransparent ? 'transparent' : isPhoto ? '#000' : bg,
      display: 'flex', flexDirection: 'column', padding: isStory ? '80px 60px 60px' : 60,
      borderRadius: 48, position: 'relative', overflow: 'hidden',
    }}>
      {isPhoto && (
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${photo})`, backgroundSize: 'cover', backgroundPosition: 'top center', backgroundRepeat: 'no-repeat' }} />
      )}
      {gradient && (
        <div style={{ position: 'absolute', inset: 0, background: gradient }} />
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent, alignItems, position: 'relative', zIndex: 1 }}>
        {children}
      </div>
      {/* Footer */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 24, borderTop: `1px solid ${effectiveTextColor === '#1a1a1a' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)'}` }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: 1 }}>trainedby</span><span style={{ fontSize: 24, fontWeight: 700, letterSpacing: 1, color: '#FF5C00' }}>RS</span><span style={{ fontSize: 24, fontWeight: 700, letterSpacing: 1 }}>.com</span>
        </div>
        {userName && <span style={{ fontSize: 22, opacity: 0.5 }}>@{userName.replace(/\s+/g, '').toLowerCase()}</span>}
      </div>
    </div>
  );
}

/* ═══ SLIDE: Big Stats Grid ═══ */
function SlideBigStats({ data, bg, textColor, photo, W, H, position }) {
  const prs = getPRs(data.exercises);
  const T = data.__t || ((_k, f) => f);
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div style={{ fontSize: 48, fontWeight: 800, marginBottom: 40, lineHeight: 1 }}>{(data.dayName || T('workoutStory.workoutFallback', 'WORKOUT')).toUpperCase()}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {[
          { v: formatDuration(data.duration), l: T('workoutStory.duration', 'Duration') },
          { v: formatVolume(data.totalVolume), l: T('workoutStory.volume', 'Volume') },
          { v: `${data.exerciseCount || 0}`, l: T('workoutStory.exercises', 'Exercises') },
          { v: prs.length > 0 ? `${prs.length}` : '—', l: T('workoutStory.records', 'Records'), hasTrophy: prs.length > 0 },
        ].map((s, i) => (
          <div key={i} style={{ padding: '20px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 52, fontWeight: 900 }}>{s.v}</span>
              {s.hasTrophy && <span style={{ fontSize: 36 }}>🏆</span>}
            </div>
            <div style={{ fontSize: 22, opacity: 0.5, letterSpacing: 1 }}>{s.l}</div>
          </div>
        ))}
      </div>
    </StoryCard>
  );
}

/* ═══ SLIDE: Exercise List ═══ */
function SlideExerciseList({ data, bg, textColor, photo, W, H, position }) {
  const prs = getPRs(data.exercises);
  const accent = '#3b82f6';
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div style={{ fontSize: 44, fontWeight: 800, marginBottom: 8, lineHeight: 1 }}>{(data.dayName || 'WORKOUT').toUpperCase()}</div>
      <div style={{ display: 'flex', gap: 32, marginBottom: 24, fontSize: 20, opacity: 0.6 }}>
        <span>Duration <strong style={{ opacity: 1 }}>{formatDuration(data.duration)}</strong></span>
        <span>Volume <strong style={{ opacity: 1 }}>{formatVolume(data.totalVolume)}</strong></span>
        {prs.length > 0 && <span>Record <strong style={{ opacity: 1 }}>{prs.length} 🏆</strong></span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {(data.exercises || []).slice(0, 8).map((ex, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 28 }}>
            <span style={{ color: accent, fontWeight: 800, minWidth: 50 }}>{ex.sets?.length || 0}x</span>
            <span style={{ fontWeight: 500 }}>{ex.name}</span>
            {ex.sets?.some(s => s.is_pr || s.pr_types?.length > 0) && (() => {
              const types = [...new Set(ex.sets?.flatMap(s => s.pr_types || (s.is_pr ? ['weight'] : [])) || [])];
              const emojis = { weight: '🏆', reps: '💪', e1rm: '⚡' };
              return <span style={{ marginLeft: 'auto', fontSize: 20 }}>{types.map(t => emojis[t] || '🏆')}</span>;
            })()}
          </div>
        ))}
        {(data.exercises || []).length > 8 && (
          <div style={{ fontSize: 22, opacity: 0.4, paddingTop: 4 }}>+{data.exercises.length - 8} more exercises</div>
        )}
      </div>
    </StoryCard>
  );
}

/* ═══ SLIDE: Centered Stats ═══ */
function SlideCenteredStats({ data, bg, textColor, photo, W, H, position }) {
  const prs = getPRs(data.exercises);
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
        <div>
          <div style={{ fontSize: 52, fontWeight: 900 }}>{formatDuration(data.duration)}</div>
          <div style={{ fontSize: 22, opacity: 0.5 }}>Duration</div>
        </div>
        <div>
          <div style={{ fontSize: 52, fontWeight: 900 }}>{formatVolume(data.totalVolume)}</div>
          <div style={{ fontSize: 22, opacity: 0.5 }}>Volume</div>
        </div>
        {prs.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <span style={{ fontSize: 52, fontWeight: 900 }}>{prs.length}</span>
              <span style={{ fontSize: 36 }}>🏆</span>
            </div>
            <div style={{ fontSize: 22, opacity: 0.5 }}>Record{prs.length !== 1 ? 's' : ''}</div>
          </div>
        )}
      </div>
    </StoryCard>
  );
}

/* ═══ SLIDE: Muscle Split Bars ═══ */
function SlideMuscleSplit({ data, bg, textColor, photo, W, H, position }) {
  const muscles = getTargetMuscles(data.exercises);
  if (muscles.length === 0) return null;
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 3, opacity: 0.5, marginBottom: 24 }}>MUSCLE SPLIT</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {muscles.slice(0, 7).map((m, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 24 }}>
              <span style={{ fontWeight: 600 }}>{dmn(m.name)}</span>
              <span style={{ fontWeight: 700, color: MC[m.name] || '#FF5C00' }}>{m.pct}%</span>
            </div>
            <div style={{ height: 14, background: textColor === '#1a1a1a' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)', borderRadius: 7, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${m.pct}%`, background: MC[m.name] || '#FF5C00', borderRadius: 7 }} />
            </div>
          </div>
        ))}
      </div>
    </StoryCard>
  );
}

/* ═══ SLIDE: PR Highlight ═══ */
function SlidePR({ pr, bg, textColor, photo, userName, W, H, position }) {
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={userName} W={W} H={H} position={position}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ fontSize: 64 }}>🏆</div>
        <div style={{ fontSize: 72, fontWeight: 900, lineHeight: 1, color: '#FF5C00' }}>{pr.weight} kg</div>
        {(() => { const types = pr.pr_types || ['weight']; const labels = { weight: 'Weight PR', reps: 'Rep PR', e1rm: '1RM PR' }; const colors = { weight: '#EAB308', reps: '#22C55E', e1rm: '#A855F7' }; return types.length > 1 ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{types.map(type => <span key={type} style={{ fontSize: 18, fontWeight: 700, color: colors[type] }}>{labels[type]}</span>)}</div> : null; })()}
        <div style={{ fontSize: 36, fontWeight: 700, marginTop: 4 }}>{pr.name}</div>
        <div style={{ fontSize: 24, opacity: 0.5 }}>Best Set · {pr.reps} reps · {pr.volume.toLocaleString()} kg volume</div>
      </div>
    </StoryCard>
  );
}

/* ═══ SLIDE: Volume Fun Fact ═══ */
function SlideVolumeFun({ data, bg, textColor, photo, W, H, position }) {
  const fact = getVolumeFunFact(data.totalVolume);
  if (!fact) return null;
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ fontSize: 28, opacity: 0.6 }}>You lifted a total of</div>
        <div style={{ fontSize: 80, fontWeight: 900, lineHeight: 1 }}>{formatVolume(data.totalVolume)}</div>
        <div style={{ fontSize: 28, opacity: 0.6, marginTop: 8 }}>{fact.text}</div>
        <div style={{ fontSize: 120, marginTop: 16 }}>{fact.emoji}</div>
      </div>
    </StoryCard>
  );
}

/* ═══ SLIDE: Radar Chart ═══ */
function SlideRadar({ data, bg, textColor, photo, W, H, position }) {
  const muscles = getTargetMuscles(data.exercises);
  if (muscles.length < 3) return null;
  const T = data.__t || ((_k, f) => f);
  const prs = getPRs(data.exercises);
  const categories = ['Chest', 'Back', 'Shoulders', 'Arms', 'Legs', 'Core'];
  const catLabel = {
    Chest: T('workoutStory.catChest', 'Chest'), Back: T('workoutStory.catBack', 'Back'),
    Shoulders: T('workoutStory.catShoulders', 'Shoulders'), Arms: T('workoutStory.catArms', 'Arms'),
    Legs: T('workoutStory.catLegs', 'Legs'), Core: T('workoutStory.catCore', 'Core'),
  };
  const catMap = { 'Chest': ['pectorals'], 'Back': ['upper back', 'lats', 'traps'], 'Shoulders': ['delts'], 'Arms': ['biceps', 'triceps', 'forearms'], 'Legs': ['quads', 'hamstrings', 'glutes', 'calves', 'adductors', 'abductors'], 'Core': ['abs'] };
  const maxSets = Math.max(...muscles.map(m => m.sets), 1);
  const catValues = categories.map(cat => {
    const related = catMap[cat] || [];
    const total = muscles.filter(m => related.includes(m.name)).reduce((acc, m) => acc + m.sets, 0);
    return Math.min(total / maxSets, 1);
  });

  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      {/* Stats row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div><div style={{ fontSize: 18, opacity: 0.5 }}>{T('workoutStory.duration', 'Duration')}</div><div style={{ fontSize: 28, fontWeight: 800 }}>{formatDuration(data.duration)}</div></div>
        <div><div style={{ fontSize: 18, opacity: 0.5 }}>{T('workoutStory.volume', 'Volume')}</div><div style={{ fontSize: 28, fontWeight: 800 }}>{formatVolume(data.totalVolume)}</div></div>
        {prs.length > 0 && <div><div style={{ fontSize: 18, opacity: 0.5 }}>{T('workoutStory.record', 'Record')}</div><div style={{ fontSize: 28, fontWeight: 800 }}>{prs.length} 🏆</div></div>}
      </div>
      {/* Radar */}
      {(() => {
        const cx = W / 2, cy = (H > W ? H * 0.35 : 440), R = Math.min(W, H) * 0.22, n = categories.length;
        const angles = categories.map((_, i) => (Math.PI * 2 * i / n) - Math.PI / 2);
        const polyPoints = catValues.map((v, i) => `${cx + Math.cos(angles[i]) * R * v},${cy + Math.sin(angles[i]) * R * v}`).join(' ');
        const lineColor = textColor === '#1a1a1a' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)';
        return (
          <svg viewBox={`0 0 ${W} ${H > W ? H * 0.6 : 880}`} style={{ width: '100%' }}>
            {[0.25, 0.5, 0.75, 1].map(s => (
              <polygon key={s} points={angles.map((a) => `${cx + Math.cos(a) * R * s},${cy + Math.sin(a) * R * s}`).join(' ')}
                fill="none" stroke={lineColor} strokeWidth="1.5" />
            ))}
            {angles.map((a, i) => (
              <line key={i} x1={cx} y1={cy} x2={cx + Math.cos(a) * R} y2={cy + Math.sin(a) * R} stroke={lineColor} strokeWidth="1.5" />
            ))}
            <polygon points={polyPoints} fill="rgba(59,130,246,0.3)" stroke="#3b82f6" strokeWidth="3" />
            {catValues.map((v, i) => (
              <circle key={i} cx={cx + Math.cos(angles[i]) * R * v} cy={cy + Math.sin(angles[i]) * R * v} r="6" fill="#3b82f6" />
            ))}
            {categories.map((cat, i) => {
              const lx = cx + Math.cos(angles[i]) * (R + 40);
              const ly = cy + Math.sin(angles[i]) * (R + 40);
              return <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fill={textColor} fontSize="24" fontFamily={FONT} fontWeight="600" opacity="0.7">{catLabel[cat] || cat}</text>;
            })}
          </svg>
        );
      })()}
    </StoryCard>
  );
}

/* ═══ SLIDE: Photo Overlay ═══ */
function SlidePhoto({ data, bg, textColor, photo, W, H, position }) {
  const muscles = getTargetMuscles(data.exercises);
  const prs = getPRs(data.exercises);
  const isStory = H > W;
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div style={{ fontSize: isStory ? 52 : 44, fontWeight: 900, lineHeight: 1, marginBottom: 10, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
        {(data.dayName || 'WORKOUT').toUpperCase()}
      </div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 14, fontSize: isStory ? 20 : 18, opacity: 0.9 }}>
        <span><strong>{formatDuration(data.duration)}</strong></span>
        <span><strong>{formatVolume(data.totalVolume)}</strong></span>
        <span><strong>{data.exerciseCount} exercises</strong></span>
        {prs.length > 0 && <span><strong>{prs.length} 🏆</strong></span>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {muscles.slice(0, 5).map((m, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px 12px', borderRadius: 999, fontSize: 16, background: `${MC[m.name] || '#FF5C00'}44`, border: `1px solid ${MC[m.name] || '#FF5C00'}66`, backdropFilter: 'blur(4px)' }}>{dmn(m.name)}</span>
        ))}
      </div>
    </StoryCard>
  );
}

/* ═══ MUSCLE: Split Focus ═══ */
function SlideSplitFocus({ data, bg, textColor, photo, W, H, position }) {
  const muscles = getTargetMuscles(data.exercises);
  if (muscles.length === 0) return null;
  const T = data.__t || ((_k, f) => f);
  const top3 = muscles.slice(0, 3);
  const primary = top3[0];
  const dayKey = {
    pectorals: 'focusChestDay', 'upper back': 'focusBackDay', lats: 'focusBackDay', delts: 'focusShoulderDay',
    biceps: 'focusArmDay', triceps: 'focusArmDay', quads: 'focusLegDay', hamstrings: 'focusLegDay',
    glutes: 'focusGluteDay', calves: 'focusLegDay', abs: 'focusCoreDay', traps: 'focusBackDay',
    forearms: 'focusArmDay', adductors: 'focusLegDay', abductors: 'focusLegDay',
  };
  const FOCUS_FALLBACK = {
    focusChestDay: 'CHEST DAY', focusBackDay: 'BACK DAY', focusShoulderDay: 'SHOULDER DAY',
    focusArmDay: 'ARM DAY', focusLegDay: 'LEG DAY', focusGluteDay: 'GLUTE DAY', focusCoreDay: 'CORE DAY',
  };
  const k = dayKey[primary.name];
  const focus = k ? T(`workoutStory.${k}`, FOCUS_FALLBACK[k]) : (data.dayName || T('workoutStory.workoutFallback', 'WORKOUT')).toUpperCase();
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 4, opacity: 0.6, marginBottom: 12 }}>TODAY'S FOCUS</div>
        <div style={{ fontSize: 76, fontWeight: 900, lineHeight: 0.9, color: '#FF5C00', marginBottom: 28 }}>{focus}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {top3.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: MC[m.name] || '#FF5C00', flexShrink: 0 }} />
              <span style={{ fontSize: 30, fontWeight: 700 }}>{dmn(m.name)}</span>
              <span style={{ fontSize: 24, opacity: 0.5, marginLeft: 'auto' }}>{m.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </StoryCard>
  );
}

/* ═══ MUSCLE: Breakdown Bars ═══ */
function SlideMuscleBreakdown({ data, bg, textColor, photo, W, H, position }) {
  const muscles = getTargetMuscles(data.exercises);
  if (muscles.length === 0) return null;
  const top6 = muscles.slice(0, 6);
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 4, opacity: 0.6, marginBottom: 28 }}>MUSCLE BREAKDOWN</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {top6.map((m, i) => (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 26 }}>
                <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{m.name}</span>
                <span style={{ fontWeight: 800, color: MC[m.name] || '#FF5C00' }}>{m.pct}%</span>
              </div>
              <div style={{ height: 18, background: 'rgba(255,255,255,0.08)', borderRadius: 9, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${m.pct}%`, background: MC[m.name] || '#FF5C00', borderRadius: 9 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </StoryCard>
  );
}

/* ═══ MUSCLE: Top 3 Podium ═══ */
function SlideTopMuscles({ data, bg, textColor, photo, W, H, position }) {
  const muscles = getTargetMuscles(data.exercises);
  if (muscles.length < 2) return null;
  const top3 = muscles.slice(0, Math.min(3, muscles.length));
  // Order: 2nd | 1st | 3rd for podium layout
  const podium = top3.length === 3 ? [top3[1], top3[0], top3[2]] : top3;
  const ranks  = top3.length === 3 ? [2, 1, 3] : [1, 2];
  const barH   = top3.length === 3 ? [160, 220, 120] : [200, 160];
  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 4, opacity: 0.6, marginBottom: 32 }}>TOP MUSCLES</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', gap: 20 }}>
          {podium.map((m, i) => {
            const rank = ranks[i];
            const h = barH[i];
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 28 }}>{medals[rank]}</div>
                <div style={{ textTransform: 'capitalize', fontSize: 20, fontWeight: 700, textAlign: 'center', maxWidth: 140 }}>{m.name}</div>
                <div style={{ fontSize: 18, color: MC[m.name] || '#FF5C00', fontWeight: 800 }}>{m.pct}%</div>
                <div style={{
                  width: 140, height: h,
                  background: `linear-gradient(180deg, ${MC[m.name] || '#FF5C00'}, ${MC[m.name] || '#FF5C00'}55)`,
                  borderRadius: '8px 8px 0 0',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  paddingTop: 12, fontSize: 32, fontWeight: 900,
                }}>{rank}</div>
              </div>
            );
          })}
        </div>
      </div>
    </StoryCard>
  );
}

/* ═══ MUSCLE: Body Map ═══ */
function SlideBodyMap({ data, bg, textColor, photo, W, H, position }) {
  const muscles = getTargetMuscles(data.exercises);
  if (muscles.length === 0) return null;
  const muscleStats = {};
  muscles.forEach(m => { muscleStats[m.name] = m.sets; });
  const maxSets = Math.max(...muscles.map(m => m.sets), 1);
  const isStory = H > W;
  const mapW = isStory ? 130 : 110;
  const mapWBack = isStory ? 118 : 100;
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <div style={{ width: mapW }}>
            <BodyView muscles={FRONT_MUSCLES} viewBox={FRONT_VB} stats={muscleStats} maxSets={maxSets} label="Front" showGradients compact colorMode="daily" />
          </div>
          <div style={{ width: mapWBack }}>
            <BodyView muscles={BACK_MUSCLES} viewBox={BACK_VB} stats={muscleStats} maxSets={maxSets} label="Back" showGradients compact colorMode="daily" />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 3, opacity: 0.6, marginBottom: 8 }}>MUSCLES</div>
          {muscles.slice(0, 7).map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: MC[m.name] || '#FF5C00', flexShrink: 0 }} />
              <span style={{ textTransform: 'capitalize', fontSize: 22, fontWeight: 600 }}>{m.name}</span>
              <span style={{ fontSize: 18, opacity: 0.5, marginLeft: 'auto' }}>{m.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </StoryCard>
  );
}

/* ═══ MUSCLE: Activity Rings ═══ */
function SlideMuscleRings({ data, bg, textColor, photo, W, H, position }) {
  const muscles = getTargetMuscles(data.exercises);
  if (muscles.length === 0) return null;
  const top5 = muscles.slice(0, 5);
  const maxSets = top5[0]?.sets || 1;
  const isStory = H > W;
  const baseR = isStory ? 110 : 95;
  const ringGap = 28;
  const sw = 20;
  const svgSize = (baseR + sw / 2) * 2 + 20;
  const cx = svgSize / 2, cy = svgSize / 2;
  return (
    <StoryCard bg={bg} textColor={textColor} photo={photo} userName={data.userName} W={W} H={H} position={position}>
      <div style={{ display: 'flex', gap: 40, alignItems: 'center' }}>
        <div style={{ flexShrink: 0 }}>
          <svg width={svgSize * 1.4} height={svgSize * 1.4} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {top5.map((m, i) => {
              const r = baseR - i * ringGap;
              const circumference = 2 * Math.PI * r;
              const pct = Math.max(0.05, m.sets / maxSets);
              const dash = pct * circumference;
              const color = MC[m.name] || '#FF5C00';
              return (
                <g key={i}>
                  <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={sw} />
                  <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
                    strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
                    transform={`rotate(-90 ${cx} ${cy})`} />
                </g>
              );
            })}
          </svg>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 3, opacity: 0.6, marginBottom: 4 }}>MUSCLE RINGS</div>
          {top5.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: MC[m.name] || '#FF5C00', flexShrink: 0 }} />
              <div>
                <div style={{ textTransform: 'capitalize', fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>{m.name}</div>
                <div style={{ fontSize: 18, opacity: 0.5 }}>{m.pct}% · {m.sets} sets</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </StoryCard>
  );
}

/* Render a slide by id — called directly in JSX so bg/position/W/H are NOT baked
   into the slides memo. This avoids rebuilding all 12 render closures on every
   position click or background change. */
function renderSlide(slide, data, bg, bgPhoto, W, H, position, prs) {
  if (!slide) return null;
  const cp = { data, bg: bg.bg, textColor: bg.text, photo: bgPhoto, W, H, position };
  switch (slide.id) {
    case 'photo':          return <SlidePhoto {...cp} />;
    case 'big-stats':      return <SlideBigStats {...cp} />;
    case 'exercise-list':  return <SlideExerciseList {...cp} />;
    case 'centered':       return <SlideCenteredStats {...cp} />;
    case 'radar':          return <SlideRadar {...cp} />;
    case 'muscle-split':   return <SlideMuscleSplit {...cp} />;
    case 'volume-fun':     return <SlideVolumeFun {...cp} />;
    case 'split-focus':    return <SlideSplitFocus {...cp} />;
    case 'muscle-breakdown': return <SlideMuscleBreakdown {...cp} />;
    case 'body-map':       return <SlideBodyMap {...cp} />;
    case 'muscle-rings':   return <SlideMuscleRings {...cp} />;
    case 'top-muscles':    return <SlideTopMuscles {...cp} />;
    default:
      if (slide.id.startsWith('pr-'))
        return <SlidePR pr={prs[slide.prIdx || 0]} bg={bg.bg} textColor={bg.text} photo={bgPhoto} userName={data.userName} W={W} H={H} position={position} />;
      return null;
  }
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function WorkoutStoryGenerator({ open, onClose, workoutData }) {
  const { t } = useTranslation();
  const { shareImage, downloadImage } = useNativeShare();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [bgOption, setBgOption] = useState('photo');
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [ratio, setRatio] = useState('story'); // 'square' | 'story'
  const [processing, setProcessing] = useState(null);
  const [contentPosition, setContentPosition] = useState('bottom-left');
  const [isCapturing, setIsCapturing] = useState(false);
  const slideRefs = useRef({});
  const captureRefs = useRef({});
  const pendingCapture = useRef(null);

  const data = { ...(workoutData || {}), userName: getDisplayName((workoutData || {}).userName), __t: t };

  // Reset to photo bg (or dark if no photo) each time the dialog opens
  React.useEffect(() => {
    if (open) {
      setBgOption(data.photo ? 'photo' : 'dark');
      setCurrentSlide(0);
      setContentPosition('bottom-left');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll when open
  React.useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  const bg = BG_OPTIONS.find(o => o.id === bgOption) || BG_OPTIONS[2];
  const bgPhoto = bgOption === 'photo' ? (data.photo || null) : null;
  const dim = RATIOS[ratio];
  const W = dim.w, H = dim.h;
  const prs = useMemo(() => getPRs(data.exercises), [data.exercises]);
  const muscles = useMemo(() => getTargetMuscles(data.exercises), [data.exercises]);
  const volFact = useMemo(() => getVolumeFunFact(data.totalVolume), [data.totalVolume]);

  // Slides array: metadata only — no render closures, no bg/position/W/H deps.
  // Changing position/background/ratio no longer rebuilds this array.
  const slides = useMemo(() => {
    const s = [];
    if (data.photo) s.push({ id: 'photo', name: 'Photo' });
    s.push({ id: 'big-stats', name: 'Stats' });
    s.push({ id: 'exercise-list', name: 'Exercises' });
    s.push({ id: 'centered', name: 'Centered' });
    if (muscles.length >= 3) s.push({ id: 'radar', name: 'Radar' });
    if (muscles.length > 0) s.push({ id: 'muscle-split', name: 'Muscles' });
    if (volFact) s.push({ id: 'volume-fun', name: 'Fun Fact' });
    prs.forEach((pr, i) => s.push({ id: `pr-${i}`, name: `PR: ${pr.name}`, prIdx: i }));
    if (muscles.length > 0) {
      s.push({ id: 'split-focus', name: 'Focus' });
      s.push({ id: 'muscle-breakdown', name: 'Breakdown' });
      s.push({ id: 'body-map', name: 'Body Map' });
      s.push({ id: 'muscle-rings', name: 'Rings' });
    }
    if (muscles.length >= 2) s.push({ id: 'top-muscles', name: 'Podium' });
    return s;
  }, [data.photo, muscles, prs, volFact]);

  const safeSlide = Math.min(currentSlide, slides.length - 1);

  const swipe = useSwipe(
    () => setCurrentSlide(s => Math.min(slides.length - 1, s + 1)),
    () => setCurrentSlide(s => Math.max(0, s - 1)),
    40,
  );

  // On-demand capture: hidden div only mounts when share/download is clicked
  React.useEffect(() => {
    if (!isCapturing || !pendingCapture.current) return;
    const mode = pendingCapture.current;
    const isTransparent = bg.id === 'transparent';
    (async () => {
      try {
        const ref = captureRefs.current[safeSlide];
        if (!ref) return;
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(ref, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: isTransparent ? null : bg.bg, width: W, height: H });
        // canvas is 2× the target size (2160×3840 for story, 2160×2160 for square) — export at full res
        if (mode === 'share') {
          await shareImage(canvas, {
            title: `${data.dayName || 'Workout'} — trainedbyRS`,
            text: 'Check out my workout on trainedbyRS!',
            fileName: 'workout.png',
          });
        } else {
          const link = document.createElement('a');
          link.download = `${(data.dayName || 'workout').replace(/\s+/g, '-').toLowerCase()}-${slides[safeSlide]?.id || 'slide'}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        }
      } catch (e) { if (e?.name !== 'AbortError') console.error(e); }
      finally { pendingCapture.current = null; setIsCapturing(false); setProcessing(null); }
    })();
  }, [isCapturing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = useCallback(() => {
    setProcessing('download');
    pendingCapture.current = 'download';
    setIsCapturing(true);
  }, []);

  const handleShare = useCallback(() => {
    setProcessing('share');
    pendingCapture.current = 'share';
    setIsCapturing(true);
  }, []);

  const handleCopyText = useCallback(() => {
    const prsText = prs.length > 0 ? `\nPersonal Records: ${prs.map(p => `${p.name} ${p.weight}kg x ${p.reps}`).join(', ')}` : '';
    const exText = (data.exercises || []).map((ex, i) => `${ex.sets?.length || 0}x ${ex.name}`).join('\n');
    const text = `${data.dayName || 'Workout'}\nDuration: ${formatDuration(data.duration)}\nVolume: ${formatVolume(data.totalVolume)}\nExercises: ${data.exerciseCount || 0}${prsText}\n\n${exText}\n\nTracked with trainedbyRS - trainedbyrs.com`;
    navigator.clipboard?.writeText(text);
  }, [data, prs]);

  // Checkered bg for transparent preview
  const previewBg = bg.id === 'transparent'
    ? 'repeating-conic-gradient(#808080 0% 25%, #666 0% 50%) 50% / 20px 20px'
    : bg.id === 'photo'
      ? '#000'
      : bg.bg;

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] bg-[#111] flex flex-col text-white" data-testid="story-generator-modal">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-white/10">
        <UITooltip>
          <TooltipTrigger asChild>
            <button onClick={() => { onClose(); setCurrentSlide(0); setShowBgPicker(false); }} className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1 flex items-center justify-center rounded-full hover:bg-white/10 active:bg-white/20 transition-colors" aria-label={t('common.back')}>
              <ArrowLeft className="h-5 w-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('common.back')}</TooltipContent>
        </UITooltip>
        <div>
          <div className="text-base font-bold leading-tight" style={{ fontFamily: FONT }}>{t('workoutStory.shareWorkout')}</div>
          <div className="text-xs text-white/40">{t('workoutStory.tagUs')}</div>
        </div>
      </div>

        {/* Slide Preview */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-2 flex flex-col items-center justify-center"
          {...{ onTouchStart: swipe.onTouchStart, onTouchMove: swipe.onTouchMove, onTouchEnd: swipe.onTouchEnd }}>
          <div className="relative mx-auto rounded-2xl overflow-hidden shadow-2xl flex-shrink-0" style={{ width: dim.previewMax, height: dim.previewMax * (H / W), background: previewBg }}>
            <div style={{ transform: `scale(${dim.previewScale})`, transformOrigin: 'top left', width: W, height: H, position: 'absolute', top: 0, left: 0 }}>
              <div ref={(el) => { slideRefs.current[safeSlide] = el; }}>
                {renderSlide(slides[safeSlide], data, bg, bgPhoto, W, H, contentPosition, prs)}
              </div>
            </div>
          </div>

          {/* Dot navigation with arrows */}
          <div className="flex items-center gap-3 mt-3">
            <UITooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
                  disabled={safeSlide === 0}
                  className="w-9 h-9 md:w-7 md:h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-20 transition-all flex-shrink-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('workoutStory.previousSlide')}</TooltipContent>
            </UITooltip>
            <div className="flex items-center gap-1.5 flex-wrap justify-center">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setCurrentSlide(i)}
                  className={`rounded-full transition-all ${i === safeSlide ? 'w-2.5 h-2.5 bg-blue-500' : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/50'}`}
                  data-testid={`dot-${i}`}
                />
              ))}
            </div>
            <UITooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setCurrentSlide(s => Math.min(slides.length - 1, s + 1))}
                  disabled={safeSlide === slides.length - 1}
                  className="w-9 h-9 md:w-7 md:h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-20 transition-all flex-shrink-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('workoutStory.nextSlide')}</TooltipContent>
            </UITooltip>
          </div>
        </div>

        {/* Hidden full-resolution render: only mounts during share/download */}
        {isCapturing && (
          <div style={{ position: 'fixed', left: '-9999px', top: '-9999px', width: W, height: H, overflow: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
            <div ref={(el) => { captureRefs.current[safeSlide] = el; }}>
              {renderSlide(slides[safeSlide], data, bg, bgPhoto, W, H, contentPosition, prs)}
            </div>
          </div>
        )}

        {/* Background Picker (collapsible) */}
        {showBgPicker && (
          <div className="px-4 pb-2 flex gap-2 flex-wrap items-center">
            {BG_OPTIONS.filter(opt => opt.id !== 'photo' || !!data.photo).map(opt => (
              <button key={opt.id} onClick={() => setBgOption(opt.id)}
                className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center overflow-hidden ${bgOption === opt.id ? 'border-blue-500 scale-110' : 'border-white/20'}`}
                style={{
                  background: opt.id === 'transparent'
                    ? 'repeating-conic-gradient(#808080 0% 25%, #555 0% 50%) 50% / 8px 8px'
                    : opt.id === 'photo' && data.photo
                      ? `url(${data.photo}) center/cover`
                      : opt.bg
                }}
                data-testid={`bg-${opt.id}`}
              >
                {opt.id === 'transparent' && <span className="text-white/60 text-[9px] font-bold">✕</span>}
              </button>
            ))}
          </div>
        )}

        {/* Bottom Toolbar (Hevy-style) */}
        <div className="flex-shrink-0 border-t border-white/10 px-2 py-3">
          <div className="flex justify-around items-start">
            {[
              { icon: ratio === 'square' ? <RectangleVertical className="h-5 w-5" /> : <Square className="h-5 w-5" />, label: ratio === 'square' ? '9:16' : '1:1', action: () => setRatio(r => r === 'square' ? 'story' : 'square'), testId: 'tb-ratio' },
              { icon: <Palette className="h-5 w-5" />, label: t('workoutStory.background'), action: () => setShowBgPicker(v => !v), active: showBgPicker, testId: 'tb-background' },
              { icon: <PositionGridIcon position={contentPosition} />, label: POS_LABEL_KEYS[contentPosition] ? t(POS_LABEL_KEYS[contentPosition]) : contentPosition, action: () => setContentPosition(p => POSITIONS[(POSITIONS.indexOf(p) + 1) % POSITIONS.length]), testId: 'tb-position' },
              { icon: <Share2 className="h-5 w-5" />, label: t('common.share'), action: handleShare, loading: processing === 'share', testId: 'tb-share', className: 'bg-primary hover:bg-[#e05200]' },
              { icon: <Download className="h-5 w-5" />, label: t('storyGenerator.download'), action: handleDownload, loading: processing === 'download', testId: 'tb-download' },
              { icon: <Copy className="h-5 w-5" />, label: t('workoutStory.copyText'), action: handleCopyText, testId: 'tb-copy' },
            ].map((btn, i) => (
              <button key={i} onClick={btn.action} disabled={btn.loading}
                className={`flex flex-col items-center gap-1.5 group ${btn.active ? 'text-blue-400' : ''}`}
                data-testid={btn.testId}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                  btn.className || 'bg-white/10 group-hover:bg-white/20'
                } ${btn.active ? 'ring-2 ring-blue-400' : ''}`}>
                  {btn.loading ? <Loader2 className="h-5 w-5 animate-spin" /> : btn.icon}
                </div>
                <span className="text-xs text-white/50 text-center leading-tight whitespace-pre-line">{btn.label}</span>
              </button>
            ))}
          </div>
        </div>
    </div>,
    document.body
  );
}
